import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Button from '@components/ui/Button';
import Card from '@components/ui/Card';
import Loading from '@components/ui/Loading';
import { getNewsById } from '@services/api/news';
import { processRichTextImages } from '@services/api/index.js';

const NewsDetailPage = () => {
  const { id } = useParams();
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const [news, setNews] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // 获取资讯详情
  useEffect(() => {
    const fetchNewsDetail = async () => {
      try {
        setLoading(true);
        const data = await getNewsById(id);
        // 空响应（后端对该语言无数据）= 暂无该语言版本
        setNews(data || { __noContent: true });
        setError(null);
      } catch (err) {
        console.error('获取资讯详情失败:', err);
        setError(t('international.detailError', '获取资讯详情失败，请稍后重试'));
      } finally {
        setLoading(false);
      }
    };

    fetchNewsDetail();
  }, [id, i18n.language]);

  // 处理返回
  const handleBack = () => {
    navigate(-1);
  };

  // 处理富文本内容中的图片路径
  const processedContent = news ? processRichTextImages(news.content) : '';

  // 判断是否有资讯详情内容需要显示
  const hasNewsDetails = news && (news.hostPlace || news.organizer || news.source);

  // 渲染加载状态
  if (loading) {
    return (
      <div className="container mx-auto px-4 py-12">
        <div className="flex justify-center">
          <Loading />
        </div>
      </div>
    );
  }

  // 渲染错误状态
  if (error || !news) {
    return (
      <div className="container mx-auto px-4 py-12">
        <div className="text-center py-12">
          <div className="text-red-500 text-4xl mb-4">❌</div>
          <p className="text-gray-600 mb-6">{error || t('international.notFound', '资讯不存在')}</p>
          <Button onClick={handleBack} className="bg-blue-600 hover:bg-blue-700 text-white">
            ← {t('common.back', '返回')}
          </Button>
        </div>
      </div>
    );
  }

  // 暂无当前语言版本（后端返回空）
  if (news.__noContent) {
    return (
      <div className="container mx-auto px-4 py-12">
        <div className="text-center py-12">
          <div className="text-gray-400 mb-4">
            <svg className="w-16 h-16 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
            </svg>
            <p className="text-lg font-medium">{t('international.noContent', '该资讯暂无当前语言版本')}</p>
          </div>
          <div className="space-x-4">
            <Button onClick={handleBack} className="bg-blue-600 hover:bg-blue-700 text-white">
              ← {t('common.back', '返回')}
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white">
      {/* 主要内容 */}
      <div className="container mx-auto px-4 py-6">
        {/* 返回按钮 */}
        <div className="mb-4">
          <Button
            variant="outline"
            onClick={handleBack}
            className="text-gray-600 border-gray-300 hover:bg-gray-100"
          >
            ← {t('international.backToList', '返回国际资讯列表')}
          </Button>
        </div>

        {/* 资讯标题和国家 */}
        <div className="mb-4 text-center">
          <h1 className="text-xl font-bold text-gray-900 mb-1">
            {news.newsName}
          </h1>
          <p className="text-gray-600 text-sm">{news.countryName}</p>
        </div>

        {/* 资讯详情 - 只有在有内容时才显示 */}
        {hasNewsDetails && (
          <div className="mb-4 border border-gray-200 rounded-md p-1 h-20">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-1 h-full">
              {news.hostPlace && (
                <div>
                  <p className="text-gray-600 text-xs">{news.hostPlace}</p>
                </div>
              )}
              {news.organizer && (
                <div>
                  <p className="text-gray-600 text-xs">{news.organizer}</p>
                </div>
              )}
              {news.source && (
                <div>
                  <p className="text-gray-600 text-xs">{news.source}</p>
                </div>
              )}
            </div>
          </div>
        )}

        {/* 书展简介 */}
        <Card className="mb-4">
          <div className="p-3">
            <div dangerouslySetInnerHTML={{ __html: processedContent }} />
          </div>
        </Card>


      </div>
    </div>
  );
};

export default NewsDetailPage;
